import { Fragment, useState } from 'react';

import { ExpandMore, InfoOutlined } from '@mui/icons-material';
import GroupsIcon from '@mui/icons-material/Groups';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';

import AutocompleteComponent from '@/components/autocomplete';
import Button from '@/components/button';
import Dialog from '@/components/dialog';
import { blockDisallowedFtsKey, sanitizeEmojis } from '@/constants/regex';
import { useFetchKeywordGroups } from '@/hooks/queries/keywords';
import { Evaluation } from '@/models/evaluations';

import {
  addItem,
  emptyGroup,
  fromWire,
  headerOperator,
  isDescendantPath,
  moveItem,
  parseKeywordsInput,
  patchItem,
  pathKey,
  pathsEqual,
  removeItem,
  setAllConnectors,
  setConnector,
  toggleOperator,
  toWire,
  type Path,
  type UIContentElement,
  type UILogicalGroup
} from '../utils/step3.helpers';
import {
  AddButtonsRow,
  ConnectorPill,
  DropZone,
  GroupBox,
  KeywordGroupCard,
  KeywordPill,
  type KeywordGroupData
} from '../utils/step3.parts';

type Step3Values = {
  keywordQuery: Evaluation.LogicalGroupElement | null;
  highlightedKeywords: Evaluation.HighlightedKeywordElement[];
};

interface Step3Props {
  onChange: (field: 'keywordQuery' | 'highlightedKeywords', value: any) => void;
  showSubtitle?: boolean;
  values: Step3Values;
  isDisabled?: boolean;
}

const isBlockElement = (el: UIContentElement) => el.type !== 'keyword';

const groupBoxSx = (
  type: 'keywordGroup' | 'logicalGroup',
  isNegated?: boolean,
  isOpen?: boolean
) => {
  if (isNegated) return { border: '1px solid #f5b5b0', bgcolor: '#fdecea' };
  if (type === 'keywordGroup' && isOpen)
    return { border: '1px solid #90caf9', bgcolor: '#e8f2fc' };
  return { border: '1px solid #ddd', bgcolor: '#fff' };
};

const Step3 = ({
  onChange,
  showSubtitle = true,
  values,
  isDisabled = false
}: Step3Props) => {
  const { data: keywordGroups = [] } = useFetchKeywordGroups({
    keywords: true
  }) as {
    data: KeywordGroupData[];
  };

  const uiRoot = fromWire(values.keywordQuery);

  const [expanded, setExpanded] = useState<string[]>([]);
  const [addKeywordAt, setAddKeywordAt] = useState<string | null>(null);
  const [addKeywordText, setAddKeywordText] = useState('');
  const [groupSelectorAt, setGroupSelectorAt] = useState<Path | null>(null);
  const [groupSelectorSearch, setGroupSelectorSearch] = useState('');
  const [groupSelectorPicked, setGroupSelectorPicked] = useState<number[]>([]);
  const [draggedPath, setDraggedPath] = useState<Path | null>(null);
  const [dropTargetKey, setDropTargetKey] = useState<string | null>(null);
  const [autoExpandedKeys, setAutoExpandedKeys] = useState<string[]>([]);
  const [termsOpen, setTermsOpen] = useState(false);

  const pluralize = (n: number, singular: string, plural = `${singular}s`) =>
    `${n} ${n === 1 ? singular : plural}`;

  const groupLabel = (n: number) => pluralize(n, 'group');
  const keywordLabel = (n: number) => pluralize(n, 'keyword');
  const itemLabel = (n: number) => pluralize(n, 'item');

  const commit = (nextUiRoot: UILogicalGroup) =>
    onChange(
      'keywordQuery',
      nextUiRoot.items.length === 0 ? null : toWire(nextUiRoot)
    );

  const toggleExpanded = (path: Path) => {
    const key = pathKey(path);
    setExpanded((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleAddKeywords = (
    path: Path,
    options?: { forceClose?: boolean }
  ) => {
    const keywords = parseKeywordsInput(addKeywordText).filter(
      (value) => value.trim().length >= 2
    );

    if (keywords.length > 0) {
      let next = uiRoot;
      keywords.forEach((value) => {
        next = addItem(next, path, {
          type: 'keyword',
          value,
          isNegated: false
        });
      });
      commit(next);
    }

    if (keywords.length > 0 || options?.forceClose) {
      setAddKeywordAt(null);
      setAddKeywordText('');
    }
  };

  const handleAddLogicalLevel = (path: Path) =>
    commit(addItem(uiRoot, path, emptyGroup()));

  const openGroupSelector = (path: Path) => {
    setGroupSelectorAt(path);
    setGroupSelectorPicked([]);
    setGroupSelectorSearch('');
  };

  const confirmGroupSelector = () => {
    if (!groupSelectorAt) return;
    let next = uiRoot;
    groupSelectorPicked.forEach((id) => {
      next = addItem(next, groupSelectorAt, {
        type: 'keywordGroup',
        id,
        operator: 'OR',
        isNegated: false
      });
    });
    commit(next);
    setGroupSelectorAt(null);
  };

  const toggleNegate = (path: Path, current?: boolean) =>
    commit(patchItem(uiRoot, path, { isNegated: !current }));
  const setItemOperator = (path: Path, operator: Evaluation.Operator) =>
    commit(patchItem(uiRoot, path, { operator }));
  const removeElement = (path: Path) => commit(removeItem(uiRoot, path));
  const setGapOperator = (
    groupPath: Path,
    gapIndex: number,
    operator: Evaluation.Operator
  ) => commit(setConnector(uiRoot, groupPath, gapIndex, operator));
  const setAllGapOperators = (groupPath: Path, operator: Evaluation.Operator) =>
    commit(setAllConnectors(uiRoot, groupPath, operator));

  const highlightedDisplay = values.highlightedKeywords.map((el) =>
    el.type === 'keyword'
      ? { title: el.value, type: 'Keyword' }
      : {
          title:
            keywordGroups.find((g) => g.id === el.id)?.title ||
            `Group #${el.id}`,
          type: 'Keyword group',
          id: el.id
        }
  );

  const pickedGroupIds = values.highlightedKeywords
    .filter(
      (e): e is { type: 'keywordGroup'; id: number } =>
        e.type === 'keywordGroup'
    )
    .map((e) => e.id);

  const availableHighlightOptions = keywordGroups
    .filter((g) => !pickedGroupIds.includes(g.id))
    .map((g) => ({ ...g, type: 'Keyword group' }));

  const handleHighlightChange = (reason: string, details: any) => {
    if (reason === 'selectOption') {
      const el: Evaluation.HighlightedKeywordElement =
        details.option.type === 'Keyword group'
          ? { type: 'keywordGroup', id: details.option.id }
          : { type: 'keyword', value: details.option.title };
      onChange('highlightedKeywords', [...values.highlightedKeywords, el]);
    } else if (reason === 'removeOption') {
      onChange(
        'highlightedKeywords',
        values.highlightedKeywords.filter((e) =>
          e.type === 'keyword'
            ? !(
                details.option.type === 'Keyword' &&
                e.value === details.option.title
              )
            : !(
                details.option.type === 'Keyword group' &&
                e.id === details.option.id
              )
        )
      );
    } else if (reason === 'clear') {
      onChange('highlightedKeywords', []);
    } else if (reason === 'createOption') {
      onChange('highlightedKeywords', [
        ...values.highlightedKeywords,
        ...parseKeywordsInput(details.option).map(
          (value): Evaluation.HighlightedKeywordElement => ({
            type: 'keyword',
            value
          })
        )
      ]);
    }
  };

  const dropZoneKey = (levelPath: Path, index: number) =>
    `${pathKey(levelPath)}:${index}`;

  const handleDragStart =
    (path: Path) => (e: React.DragEvent<HTMLDivElement>) => {
      const { dataTransfer } = e;
      e.stopPropagation();
      dataTransfer.effectAllowed = 'move';
      dataTransfer.setData('text/plain', pathKey(path));
      setDraggedPath(path);
    };

  const handleDragEnd = () => {
    setDraggedPath(null);
    setDropTargetKey(null);
    collapseAutoExpanded();
  };

  const handleDropZoneOver =
    (levelPath: Path, index: number) =>
    (e: React.DragEvent<HTMLDivElement>) => {
      if (!draggedPath) return;
      const { dataTransfer } = e;
      e.preventDefault();
      e.stopPropagation();
      dataTransfer.dropEffect = 'move';
      const key = dropZoneKey(levelPath, index);
      if (dropTargetKey !== key) setDropTargetKey(key);
    };

  const handleDropZoneLeave = () => setDropTargetKey(null);

  const handleGroupHoverExpand =
    (path: Path) => (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      const key = pathKey(path);
      if (expanded.includes(key)) return;

      setExpanded((prev) => [...prev, key]);
      setAutoExpandedKeys((prev) =>
        prev.includes(key) ? prev : [...prev, key]
      );
    };

  const collapseAutoExpanded = () => {
    setExpanded((prev) => prev.filter((k) => !autoExpandedKeys.includes(k)));
    setAutoExpandedKeys([]);
  };

  const handleDropZoneDrop =
    (levelPath: Path, index: number) =>
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setDropTargetKey(null);
      setAutoExpandedKeys((prev) =>
        prev.filter((k) => k !== pathKey(levelPath))
      );
      if (!draggedPath) return;
      commit(moveItem(uiRoot, draggedPath, levelPath, index));
      setDraggedPath(null);
    };

  const renderElement = (element: UIContentElement, path: Path) => {
    const key = pathKey(path);
    const isDragging = !!draggedPath && pathsEqual(draggedPath, path);

    if (element.type === 'keyword') {
      return (
        <KeywordPill
          label={element.value}
          isNegated={element.isNegated}
          onToggle={() => toggleNegate(path, element.isNegated)}
          onDelete={() => removeElement(path)}
          disabled={isDisabled}
          draggable={!isDisabled}
          onDragStart={handleDragStart(path)}
          onDragEnd={handleDragEnd}
          isDragging={isDragging}
        />
      );
    }

    const isOpen = expanded.includes(key);

    if (element.type === 'keywordGroup') {
      const group = keywordGroups.find((g) => g.id === element.id);
      const keywordsCount = group?.keywords?.length ?? 0;
      return (
        <GroupBox
          accentSx={groupBoxSx('keywordGroup', element.isNegated, isOpen)}
          isNegated={element.isNegated}
          isOpen={isOpen}
          onToggleExpand={() => toggleExpanded(path)}
          onToggleNegate={() => toggleNegate(path, element.isNegated)}
          subtitle={
            <>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ fontWeight: 700 }}
              >
                KEYWORD GROUP
              </Typography>
              <Typography variant="body2">
                {group?.title || `Group #${element.id}`}
              </Typography>
            </>
          }
          badge={<Chip size="small" label={keywordLabel(keywordsCount)} />}
          operator={element.operator}
          onSetOperator={(op) => setItemOperator(path, op)}
          onRemove={() => removeElement(path)}
          disabled={isDisabled}
          draggable={!isDisabled}
          onDragStart={handleDragStart(path)}
          onDragEnd={handleDragEnd}
          isDragging={isDragging}
          operatorDisabled={keywordsCount <= 1}
        >
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {group?.keywords?.map((kw) => (
              <KeywordPill key={kw} label={kw} disabled />
            ))}
          </Stack>
        </GroupBox>
      );
    }

    const gapOperator = headerOperator(element.connectors);
    const canHoverExpand =
      !!draggedPath &&
      !isOpen &&
      !pathsEqual(draggedPath, path) &&
      !isDescendantPath(draggedPath, path);

    return (
      <GroupBox
        accentSx={groupBoxSx('logicalGroup', element.isNegated, isOpen)}
        isNegated={element.isNegated}
        isOpen={isOpen}
        onToggleExpand={() => toggleExpanded(path)}
        onToggleNegate={() => toggleNegate(path, element.isNegated)}
        subtitle={
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ fontWeight: 700 }}
          >
            LOGICAL LEVEL
          </Typography>
        }
        badge={
          element.items.length === 0 ? (
            <Chip
              size="small"
              label="No items"
              color="warning"
              variant="outlined"
            />
          ) : (
            <Chip size="small" label={itemLabel(element.items.length)} />
          )
        }
        operator={gapOperator}
        onSetOperator={(op) => setAllGapOperators(path, op)}
        onRemove={() => removeElement(path)}
        disabled={isDisabled}
        draggable={!isDisabled}
        onDragStart={handleDragStart(path)}
        onDragEnd={handleDragEnd}
        isDragging={isDragging}
        onHoverDragEnter={
          canHoverExpand ? handleGroupHoverExpand(path) : undefined
        }
        operatorDisabled={element.items.length <= 1}
      >
        {renderLevel(element, path)}
      </GroupBox>
    );
  };

  const renderLevel = (group: UILogicalGroup, path: Path) => (
    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
      {!!draggedPath && (
        <DropZone
          isDragActive={!!draggedPath}
          isHovered={dropTargetKey === dropZoneKey(path, 0)}
          onDragOver={handleDropZoneOver(path, 0)}
          onDrop={handleDropZoneDrop(path, 0)}
          onDragLeave={handleDropZoneLeave}
        />
      )}
      {group.items.map((el, i) => (
        <Fragment key={pathKey([...path, i])}>
          <Box sx={isBlockElement(el) ? { flexBasis: '100%' } : undefined}>
            {renderElement(el, [...path, i])}
          </Box>
          {i < group.items.length - 1 && (
            <ConnectorPill
              operator={group.connectors[i]}
              onToggle={() =>
                setGapOperator(path, i, toggleOperator(group.connectors[i]))
              }
              disabled={isDisabled}
            />
          )}
          <DropZone
            isDragActive={!!draggedPath}
            isHovered={dropTargetKey === dropZoneKey(path, i + 1)}
            onDragOver={handleDropZoneOver(path, i + 1)}
            onDrop={handleDropZoneDrop(path, i + 1)}
            onDragLeave={handleDropZoneLeave}
          />
        </Fragment>
      ))}
      <Box sx={{ flexBasis: '100%' }}>
        <AddButtonsRow
          disabled={isDisabled}
          onAddKeyword={() => {
            setAddKeywordAt(pathKey(path));
            setAddKeywordText('');
          }}
          onAddGroup={() => openGroupSelector(path)}
          onAddLevel={() => handleAddLogicalLevel(path)}
          showInput={addKeywordAt === pathKey(path)}
          inputValue={addKeywordText}
          onInputChange={setAddKeywordText}
          onInputKeyDown={(key) => {
            if (key === 'Enter') handleAddKeywords(path);
            if (key === 'Escape') setAddKeywordAt(null);
          }}
          onInputBlur={() => handleAddKeywords(path, { forceClose: true })}
        />
      </Box>
    </Stack>
  );

  return (
    <>
      <Box sx={{ p: '8px 16px' }}>
        {showSubtitle && (
          <Typography variant="body2" sx={{ mb: 2 }}>
            Please add the keywords and keyword groups that you would like to
            search for.
          </Typography>
        )}
        <Stack sx={{ backgroundColor: 'background.paper' }}>
          <Box sx={{ border: '1px solid #ccc', borderRadius: 1 }}>
            <Stack sx={{ p: 1.5, borderBottom: '1px solid #eee' }}>
              <Typography>Query</Typography>
            </Stack>
            <Box sx={{ p: 1.5 }}>{renderLevel(uiRoot, [])}</Box>
          </Box>
        </Stack>

        <Accordion
          expanded={termsOpen}
          onChange={(_e, isExpanded) => setTermsOpen(isExpanded)}
          disableGutters
          sx={{
            mt: 4,
            border: '1px solid #ddd',
            borderRadius: 1,
            backgroundColor: 'background.paper',
            boxShadow: 'none',
            '&:before': { display: 'none' }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            id="evaluation-additional-terms"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography>Additional terms</Typography>
              <Tooltip
                placement="right"
                arrow
                title="These terms are not part of the search query, so the resulting posts may not contain them. If these terms do appear, they will be highlighted just like keywords."
              >
                <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                  <InfoOutlined fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            </Stack>
          </AccordionSummary>

          <AccordionDetails sx={{ pt: 0 }}>
            <AutocompleteComponent
              size="medium"
              value={highlightedDisplay}
              options={availableHighlightOptions}
              freeSolo
              groupBy={(option: any) => option.type}
              onChange={(_e: any, value: any, reason: string, details: any) =>
                handleHighlightChange(reason, details)
              }
              getOptionLabel={(option: any) => option.title}
              isOptionEqualToValue={(option: any, value: any) =>
                `${option.type}::${option.title}` ===
                `${value.type}::${value.title}`
              }
              renderInput={(params: any) => (
                <TextField
                  {...params}
                  size="medium"
                  variant="outlined"
                  label="keywords or keyword groups"
                  onKeyDown={blockDisallowedFtsKey}
                  inputProps={{
                    ...params.inputProps,
                    onInput: ({ currentTarget }) => {
                      const input = currentTarget;
                      input.value = sanitizeEmojis(input.value);
                    }
                  }}
                />
              )}
              renderOption={(props: any, option: any) => (
                <Stack
                  key={option.id}
                  direction="row"
                  alignItems="center"
                  {...props}
                >
                  <Typography>{option.title}</Typography>
                  {option.type === 'Keyword group' && (
                    <Typography color="textSecondary" ml="auto" variant="body2">
                      {option.keywords?.length}
                    </Typography>
                  )}
                </Stack>
              )}
              renderTags={(tags: any[], getTagProps: any) =>
                tags.map((tag, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={`hl-${tag.type}-${tag.title}-${index}`}
                    label={tag.title}
                    size="small"
                    icon={
                      tag.type === 'Keyword group' ? <GroupsIcon /> : undefined
                    }
                  />
                ))
              }
              disabled={isDisabled}
            />
          </AccordionDetails>
        </Accordion>

        <Dialog
          maxWidth="lg"
          fullWidth
          open={!!groupSelectorAt}
          onClose={() => setGroupSelectorAt(null)}
          dialogContent={
            <Box sx={{ minWidth: 500 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Available keyword groups
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search for keyword group"
                value={groupSelectorSearch}
                onChange={(e) => setGroupSelectorSearch(e.target.value)}
                sx={{ mb: 2 }}
              />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 2,
                  maxHeight: 500,
                  overflowY: 'auto',
                  p: 1
                }}
              >
                {keywordGroups
                  .filter(
                    (g) =>
                      g.keywords.length > 0 &&
                      g.title
                        .toLowerCase()
                        .includes(groupSelectorSearch.toLowerCase())
                  )
                  .map((g) => (
                    <KeywordGroupCard
                      key={g.id}
                      group={g}
                      selected={groupSelectorPicked.includes(g.id)}
                      onToggle={() =>
                        setGroupSelectorPicked((prev) =>
                          prev.includes(g.id)
                            ? prev.filter((id) => id !== g.id)
                            : [...prev, g.id]
                        )
                      }
                    />
                  ))}
              </Box>
              <Stack
                direction="row"
                justifyContent="flex-end"
                spacing={1}
                sx={{ mt: 2 }}
              >
                <Button
                  text="Cancel"
                  variant="text"
                  onClick={() => setGroupSelectorAt(null)}
                />
                <Button
                  text={`Add ${groupLabel(groupSelectorPicked.length)}`}
                  variant="contained"
                  disabled={groupSelectorPicked.length === 0}
                  onClick={confirmGroupSelector}
                >
                  Add {groupLabel(groupSelectorPicked.length)}
                </Button>
              </Stack>
            </Box>
          }
        />
      </Box>
    </>
  );
};

export default Step3;
